import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { ScrollTop } from '@/components/element/ScrollTop';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { BillingSchedulesElement } from '@/features/w/[identifier]/teams/[team_id]/sales_orders/[sales_order_id]/billing_schedules/{components}/BillingSchedulesElement';
import { useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';
import { useBillingSchedules, useBillingSchedulesInvalidate } from '@/hooks/api/1350_billing_schedules';
import { useCreateBillingSchedule } from '@/hooks/api/1360_create_billing_schedule';
import { BillingScheduleLogic, SalesOrderLogic, TeamMemberLogic } from '@/hooks/api/models/logics';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentSalesOrder } from '@/providers/SalesOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SalesOrderAccountingsElement } from '@/features/w/[identifier]/teams/[team_id]/sales_orders/[sales_order_id]/billing_schedules/{components}/SalesOrderAccountingsElement';
import { ManualModeEditElement } from '@/features/w/[identifier]/teams/[team_id]/sales_orders/[sales_order_id]/billing_schedules/{components}/ManualModeEditElement';
import { useCreateSalesOrderAccounting } from '@/hooks/api/1400_create_sales_order_accountings';
import { useSalesOrderAccountingsInvalidate } from '@/hooks/api/1390_sales_order_accountings';

type FormInputs = {
  identifier: string;
  team_id: number;
  sales_order_id: number;
  year_month: string;
  monthly_amount?: number;
  spot_amount?: number;
};

export const BillingSchedules = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { _so_ } = useCurrentSalesOrder();
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle(`売上`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `プロジェクト（売上） - ${_t_!.name}`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders` },
        { label: `プロジェクト（売上）詳細`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}` },
      ]),
    []
  );

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 mx-auto">
            <_BillingSchedules />

            <_SalesOrderAccountings />
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

const _BillingSchedules = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember } = useCurrentTeamMember();

  const location = useLocation();
  const { _so_ } = useCurrentSalesOrder();
  const { data: billingSchedules } = useBillingSchedules({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { mutate, data, isPending, error } = useCreateBillingSchedule();
  const { invalidate } = useBillingSchedulesInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });

  const [ymOptions, setYmOptions] = useState<string[]>([]);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
    },
  });

  const isEditable = useMemo(() => {
    if (meTeamMember && TeamMemberLogic.hasApproverRole(meTeamMember)) {
      return true;
    } else {
      return !_so_!.closed;
    }
  }, [meTeamMember, _so_]);

  useEffect(() => {
    if (billingSchedules) {
      setYmOptions(BillingScheduleLogic.yearMonthOptions(billingSchedules));
    }
  }, [billingSchedules]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidateSalesOrder();
    }
  }, [data]);

  return (
    <div className="card mb-4" id="basic-info">
      <div className="card-header d-flex align-items-center">
        <div className="col-md-8">
          <h5 className="mb-1">売上</h5>
          <p className="text-xs mb-0">売上予定と実績の管理</p>
        </div>
      </div>
      <div className="card-body pt-0 row">
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label>プロジェクト名</label>
            <p className="form-text text-muted text-sm ms-1">{_so_!.name}</p>
          </div>
        </div>
        <hr className="horizontal dark my-2" />

        <form onSubmit={handleSubmit(onSubmit)} className="text-start">
          <div className="accordion" id="accordion">
            <div className="accordion-item">
              <h5 className="accordion-header text-sm text-decoration-underline" id="headingOne">
                <button
                  className="accordion-button font-weight-bold collapsed px-0"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseOne"
                  aria-expanded="false"
                  aria-controls="collapseOne"
                >
                  請求月を追加する場合はこちら
                  <i className="collapse-close fa fa-plus text-xs pt-1 position-absolute end-0" aria-hidden="true"></i>
                  <i className="collapse-open fa fa-minus text-xs pt-1 position-absolute end-0" aria-hidden="true"></i>
                </button>
              </h5>
              <div id="collapseOne" className="accordion-collapse collapse row" data-bs-parent="#accordion">
                <ApiErrorMessages error={error} />
                <div className="col-12 col-md-3">
                  <FormGroup label="請求月" required={true}>
                    <input
                      type="text"
                      maxLength={7}
                      className={`form-control ${errors.year_month && 'is-invalid'}`}
                      placeholder={'YYYY/MM'}
                      list="billing-date"
                      disabled={!isEditable}
                      {...register('year_month', {
                        required: '入力して下さい。',
                        pattern: {
                          value: /^[0-9]{4}\/(0[1-9]|1[0-2])$/,
                          message: 'YYYY/MM形式で入力して下さい。',
                        },
                      })}
                    />
                    <datalist id="billing-date">
                      {ymOptions.map((d) => (
                        <option key={`ym-${d}`} value={d}></option>
                      ))}
                    </datalist>
                    <div className="text-xs text-muted mt-1">請求を実施する年月</div>
                  </FormGroup>
                </div>
                <div className="col-12 col-md-3">
                  <FormGroup label="一括請求金額（税抜）">
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} disabled={!isEditable} />
                        )}
                        control={control}
                        name="spot_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">納品時等一括で請求する金額</div>
                  </FormGroup>
                </div>
                <div className="col-12 col-md-3">
                  <FormGroup label="月額請求金額（税抜）">
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} disabled={!isEditable} />
                        )}
                        control={control}
                        name="monthly_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">&nbsp;</div>
                  </FormGroup>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button className="btn w-100 btn-icon bg-gradient-dark" disabled={!isEditable}>
                      <i className="fa fa-plus"></i>追加
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        <hr className="horizontal dark my-3" />

        <BillingSchedulesElement />
      </div>
      <div className="card-footer d-flex align-items-center justify-content-end pt-0">
        <Link
          to={
            location?.state?.prevPath
              ? location?.state?.prevPath
              : `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}`
          }
          className="btn btn-outline-secondary"
        >
          案件詳細へ
        </Link>
      </div>
    </div>
  );
};

const _SalesOrderAccountings = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle(`売上（管理会計）`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `プロジェクト（売上） - ${_t_!.name}`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders` },
        { label: `プロジェクト（売上）詳細`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${_so_!.id}` },
      ]),
    []
  );

  const { setShowLoading } = useLoading();
  const { _so_ } = useCurrentSalesOrder();
  const { mutate, data, isPending, error } = useCreateSalesOrderAccounting();
  const { invalidate } = useSalesOrderAccountingsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });
  const { invalidate: invalidateSalesOrder } = useSalesOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _so_!.id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: _so_!.id,
      spot_amount: 0,
      monthly_amount: 0,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidateSalesOrder();
    }
  }, [data]);

  return (
    <div className="card mb-4" id="basic-info">
      <div className="card-header d-flex align-items-center">
        <div className="col-md-8">
          <h5 className="mb-1">手動管理モード</h5>
          <p className="text-xs mb-0">
            手動管理モードをONにすると、請求実績とは独立して管理会計を編集できます。
            <br />
            請求月と計上月を変えたい場合は、手動管理モードにしてください。
            <br />
            一度手動管理モードにすると、元のモードに戻すことはできません。
          </p>
        </div>
      </div>
      <div className="card-body pt-0 row">
        <ManualModeEditElement />

        {_so_!.accounting_manual_mode && SalesOrderLogic.canUpdateAccounting(_t_!, me!) && (
          <>
            <form onSubmit={handleSubmit(onSubmit)} className="text-start">
              <div className="row">
                <ApiErrorMessages error={error} />

                <div className="col-12 col-md-3">
                  <FormGroup label="計上月" required={true}>
                    <input
                      type="text"
                      maxLength={7}
                      className={`form-control ${errors.year_month && 'is-invalid'}`}
                      placeholder={'YYYY/MM'}
                      {...register('year_month', {
                        required: '入力して下さい。',
                        pattern: {
                          value: /^[0-9]{4}\/(0[1-9]|1[0-2])$/,
                          message: 'YYYY/MM形式で入力して下さい。',
                        },
                      })}
                    />
                    <div className="text-xs text-muted mt-1">計上の対象となる年月</div>
                  </FormGroup>
                </div>
                <div className="col-12 col-md-3">
                  <FormGroup label="一括請求金額（税抜）">
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} />
                        )}
                        control={control}
                        name="spot_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">管理会計上の一括で計上する金額</div>
                  </FormGroup>
                </div>
                <div className="col-12 col-md-3">
                  <FormGroup label="月額請求金額（税抜）">
                    <div className="input-group me-2">
                      <Controller
                        render={({ field: { onChange, value, ...field } }) => (
                          <InputPrice onChange={onChange} value={value} />
                        )}
                        control={control}
                        name="monthly_amount"
                      />
                    </div>
                    <div className="text-xs text-muted mt-1">管理会計上月額で計上する金額</div>
                  </FormGroup>
                </div>
                <div className="col-12 col-md-3">
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button className="btn w-100 btn-icon bg-gradient-dark">
                      <i className="fa fa-plus"></i>追加
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </>
        )}

        {_so_!.accounting_manual_mode && (
          <>
            <hr className="horizontal dark my-3" />

            <SalesOrderAccountingsElement />
          </>
        )}
      </div>
    </div>
  );
};
